import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import AgentAPI from '../../api/agents';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getAgents($state) {
    return $state.records;
  },
  getVerifiedAgents($state) {
    return $state.records.filter(record => record.confirmed);
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getAgentById: $state => id => {
    return $state.records.find(record => record.id === Number(id)) || {};
  },
  getAgentStatus($state) {
    let status = {
      online: $state.records.filter(
        agent => agent.availability_status === 'online'
      ).length,
      busy: $state.records.filter(agent => agent.availability_status === 'busy')
        .length,
      offline: $state.records.filter(
        agent => agent.availability_status === 'offline'
      ).length,
    };
    return status;
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.default.SET_AGENT_FETCHING_STATUS, true);
    try {
      const response = await AgentAPI.get();
      const filtered = response.data.filter(
        agent =>
          !agent.email.match(/chat_\d+@((robotjara\.cz)|(jarabot\.com))/i)
      );
      commit(types.default.SET_AGENTS, filtered);
    } finally {
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
    }
  },
  create: async ({ commit }, agentInfo) => {
    commit(types.default.SET_AGENT_CREATING_STATUS, true);
    try {
      const response = await AgentAPI.create(agentInfo);
      commit(types.default.ADD_AGENT, response.data);
    } finally {
      commit(types.default.SET_AGENT_CREATING_STATUS, false);
    }
  },
  update: async ({ commit }, { id, ...agentParams }) => {
    commit(types.default.SET_AGENT_UPDATING_STATUS, true);
    try {
      const response = await AgentAPI.update(id, agentParams);
      commit(types.default.EDIT_AGENT, response.data);
    } finally {
      commit(types.default.SET_AGENT_UPDATING_STATUS, false);
    }
  },
  updateSingleAgentPresence: ({ commit }, { id, availabilityStatus }) => {
    commit(types.default.UPDATE_SINGLE_AGENT_PRESENCE, {
      id,
      availabilityStatus,
    });
  },
  updatePresence: async ({ commit }, data) => {
    commit(types.default.UPDATE_AGENTS_PRESENCE, data);
  },
  updatePhoneStatus: async ({ commit }, data) => {
    commit(types.default.UPDATE_AGENT_PHONE_STATUS, data);
  },
  setPhoneQueueStatuses: ({ commit }, data) => {
    commit(types.default.UPDATE_AGENT_PHONE_QUEUE_STATUSES, data);
  },
  delete: async ({ commit }, agentId) => {
    commit(types.default.SET_AGENT_DELETING_STATUS, true);
    try {
      await AgentAPI.delete(agentId);
      commit(types.default.DELETE_AGENT, agentId);
    } finally {
      commit(types.default.SET_AGENT_DELETING_STATUS, false);
    }
  },
};

export const mutations = {
  [types.default.SET_AGENT_FETCHING_STATUS]($state, status) {
    $state.uiFlags.isFetching = status;
  },
  [types.default.SET_AGENT_CREATING_STATUS]($state, status) {
    $state.uiFlags.isCreating = status;
  },
  [types.default.SET_AGENT_UPDATING_STATUS]($state, status) {
    $state.uiFlags.isUpdating = status;
  },
  [types.default.SET_AGENT_DELETING_STATUS]($state, status) {
    $state.uiFlags.isDeleting = status;
  },

  [types.default.SET_AGENTS]: MutationHelpers.set,
  [types.default.ADD_AGENT]: MutationHelpers.create,
  [types.default.EDIT_AGENT]: MutationHelpers.update,
  [types.default.DELETE_AGENT]: MutationHelpers.destroy,
  [types.default.UPDATE_AGENTS_PRESENCE]: MutationHelpers.updatePresence,
  [types.default.UPDATE_SINGLE_AGENT_PRESENCE]: (
    $state,
    { id, availabilityStatus }
  ) =>
    MutationHelpers.updateSingleRecordPresence($state.records, {
      id,
      availabilityStatus,
    }),
  [types.default.UPDATE_AGENT_PHONE_STATUS]: (
    $state,
    { user_id, status, dnd, queue_status }
  ) => {
    const agentIndex = $state.records.findIndex(agent => agent.id === user_id);
    if (agentIndex !== -1) {
      const agent = $state.records[agentIndex];
      if (status) {
        agent.phone_status = status;
      }
      if (dnd !== undefined) {
        agent.dnd = dnd;
      }
      if (queue_status && agent.phone_queue_status) {
        const index = agent.phone_queue_status.findIndex(
          qs => qs.number === queue_status.number
        );
        if (index >= 0) {
          agent.phone_queue_status[index] = queue_status;
        } else {
          agent.phone_queue_status.push(queue_status);
          agent.phone_queue_status.sort((a, b) =>
            a.number.localeCompare(b.number)
          );
        }
      }
    }
  },
  [types.default.UPDATE_AGENT_PHONE_QUEUE_STATUSES]: (
    $state,
    { user_id, status, queue_status }
  ) => {
    const agentIndex = $state.records.findIndex(agent => agent.id === user_id);
    if (agentIndex === -1) return;
    const agent = $state.records[agentIndex];
    if (status) {
      agent.phone_status = status;
    }
    if (Array.isArray(queue_status)) {
      agent.phone_queue_status = queue_status;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
